import React, { useState } from "react";
import { format } from "date-fns";
import PropTypes from "prop-types";
import { useAddStatus } from "api/tes/useAddStatus";
import { Button } from "components/button";
import MessageBanner from "components/message-banner";

const ManagePaymentModal = ({
  isOpen,
  onClose,
  takeback,
  onSuccess = () => {},
}) => {
  const [isResending, setIsResending] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);

  // Get payment information from takeback
  const payment = takeback?.payments_list?.[0];
  const paymentMetadata = payment?.payments_metadata_list || [];

  // Get email from metadata
  const emailMetadata = paymentMetadata.find((m) => m.key === "email");
  const email =
    emailMetadata?.value || takeback?.customer_id_belongsTo?.email || "";

  // Get verified value (amount)
  const verifiedValue =
    paymentMetadata.find((m) => m.key === "verified_value")?.value ||
    paymentMetadata.find((m) => m.key === "amount")?.value ||
    "$42.40";

  // Find the latest gift card resend event from processing history
  const latestResend = takeback?.takeback_processing_history_list
    ?.filter((h) => h.status === "associate-resend-giftcard")
    ?.sort((a, b) => b.updated_at - a.updated_at)?.[0];

  const formatDate = (timestamp) => {
    if (!timestamp) return "";
    const date = new Date(timestamp * 1000);
    return format(date, "MMMM d, yyyy 'at' h:mm a");
  };

  // Use the existing mutation hook for adding status
  const { mutate: addStatus } = useAddStatus();

  const handleResendEmail = () => {
    setIsResending(true);

    addStatus(
      {
        id: takeback.id,
        input: {
          status: "associate-resend-giftcard",
          parent_status: "processed",
          updated_at: Math.floor(Date.now() / 1000),
        },
      },
      {
        onSuccess: () => {
          setIsResending(false);
          setShowSuccessMessage(true);
          // Hide success message after 3 seconds
          setTimeout(() => {
            setShowSuccessMessage(false);
          }, 10000);
          if (onSuccess) {
            onSuccess();
          }
        },
        onError: (error) => {
          setIsResending(false);
          console.error("Error resending gift card:", error);
          alert("Failed to resend gift card email. Please try again.");
        },
      }
    );
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-full p-4">
        <div
          className="fixed inset-0 bg-black bg-opacity-50"
          onClick={onClose}
        />
        <div className="relative bg-white rounded-2xl shadow-xl max-w-md w-full">
          {/* Header */}
          <div className="flex items-center justify-between p-6 pb-0">
            <h2 className="text-2xl font-semibold">Manage payment</h2>
            <button
              onClick={onClose}
              className="p-1 rounded-full hover:bg-gray-100 transition-colors"
              aria-label="Close"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M15 5L5 15M5 5L15 15"
                  stroke="#6B7280"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>

          {/* Content */}
          <div className="p-6">
            {/* Payment Information */}
            <div className="mb-6">
              <h3 className="text-base font-semibold text-gray-900 mb-4">
                Payment Information
              </h3>

              <div className="space-y-3">
                <div>
                  <div className="text-sm text-black font-bold mb-1">
                    First issued
                  </div>
                  <div className="text-sm text-gray-900">
                    {formatDate(payment?.created_at)}
                  </div>
                </div>

                <div>
                  <div className="text-sm text-black font-bold mb-1">
                    Original email address
                  </div>
                  <div className="text-sm text-gray-900">{email}</div>
                </div>

                <div>
                  <div className="text-sm text-black font-bold mb-1">
                    Verified value
                  </div>
                  <div className="text-sm text-gray-900">
                    {verifiedValue} LEGO® e-Gift Card
                  </div>
                </div>
              </div>
            </div>

            {/* Resend Section */}
            <div className="border-t border-gray-300 pt-6">
              <h3 className="text-base font-semibold text-gray-900 mb-4">
                Resend
              </h3>

              {latestResend && (
                <div className="mb-4">
                  <div className="text-sm text-black font-bold mb-1">
                    Last sent
                  </div>
                  <div className="text-sm text-gray-900">
                    {formatDate(latestResend.updated_at)}
                  </div>
                </div>
              )}

              <div className="text-sm text-black font-bold mb-4">
                Resend email to
              </div>

              <div className="flex items-center gap-3">
                <input
                  type="email"
                  value={email}
                  readOnly
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-700"
                />
                <Button
                  onClick={handleResendEmail}
                  disabled={isResending}
                  variant="tertiary"
                  size="small"
                >
                  {isResending ? "Sending..." : "Resend Email"}
                </Button>
              </div>

              {showSuccessMessage && (
                <div className="mt-6">
                  <MessageBanner
                    colour="green"
                    title="e-Gift Card email sent successfully!"
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

ManagePaymentModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  takeback: PropTypes.object.isRequired,
  onSuccess: PropTypes.func,
};

export default ManagePaymentModal;
